import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { getAdminApiBaseUrl, normalizeAdminRequestPath } from './adminApiPaths'
import { assertSessionGeneration, canRefreshAccessToken, clearAccessToken, getAccessToken, getSessionGeneration, refreshAuthSession } from './authSession'
import { getStoredCurrentOrg } from '../lib/currentOrgStorage'

const TRAILING_SLASHES_PATTERN = /\/+$/
const AUTH_MUTATION_TIMEOUT_MS = 30_000

type ViteImportMeta = ImportMeta & {
  env?: Record<string, string | undefined>
}

type AdminRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _skipAuthRefresh?: boolean
  _skipOrgRewrite?: boolean
  _authGeneration?: number
  _authRefreshAllowed?: boolean
  _authMutation?: boolean
  _capturedAuthToken?: string | null
}

type AuthRefreshConfig = AxiosRequestConfig & {
  _skipAuthRefresh?: boolean
  _authGeneration?: number
  _authMutation?: boolean
}

// Use environment variable for API URL, fallback to relative path
const viteEnv = (import.meta as ViteImportMeta).env || {}

export function getAdminApiBaseUrlFromEnv(env: Record<string, string | undefined> = viteEnv): string {
  return getAdminApiBaseUrl(env.VITE_API_URL || '')
}

export function getFeedApiBaseUrlFromEnv(env: Record<string, string | undefined> = viteEnv): string {
  return (env.VITE_FEED_API_URL || '').replace(TRAILING_SLASHES_PATTERN, '')
}

const FEED_API_URL = getFeedApiBaseUrlFromEnv(viteEnv)

const api: AxiosInstance = axios.create({
  baseURL: getAdminApiBaseUrlFromEnv(viteEnv),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

const feedClient: AxiosInstance = axios.create()

api.interceptors.request.use(
  (config: AdminRequestConfig) => {
    // A stalled cookie writer must abort its actual transport before the queue
    // advances. Axios's browser timeout covers the response body as well.
    if (config._authMutation) config.timeout = AUTH_MUTATION_TIMEOUT_MS
    config._authGeneration ??= getSessionGeneration()
    config._authRefreshAllowed ??= canRefreshAccessToken()
    // Logout and its private refresh use captured credentials after earlier
    // cookie mutations settle, even if a newer login is already queued.
    const capturedAuth = config._capturedAuthToken !== undefined
    if (!capturedAuth) assertSessionGeneration(config._authGeneration)
    const token = capturedAuth ? config._capturedAuthToken : getAccessToken()
    if (token) config.headers.Authorization = `Bearer ${token}`
    else config.headers.delete('Authorization')

    const currentOrg = getStoredCurrentOrg()

    // Handle :org placeholder
    if (config.url?.includes(':org')) {
      if (currentOrg?.slug) {
        config.url = config.url.replace(':org', currentOrg.slug)
      } else {
        console.warn('No organization selected for request:', config.url)
        throw new Error('No organization selected')
      }
    }

    config.url = normalizeAdminRequestPath(
      config.url,
      currentOrg?.slug || '',
      Boolean(config._skipOrgRewrite)
    )

    return config
  },
  error => { throw error },
  // Capture ownership at the API call, before a same-turn login/logout can
  // change accounts while Axios is preparing the original request.
  { synchronous: true }
)

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const config = response.config as AdminRequestConfig
    if (!config._authMutation) assertSessionGeneration(config._authGeneration ?? getSessionGeneration())
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AdminRequestConfig | undefined
    if (!originalRequest) {
      return Promise.reject(error)
    }

    const requestUrl = String(originalRequest?.url || '')
    const skipAuthRefresh =
      Boolean(originalRequest?._skipAuthRefresh) ||
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh')

    const generation = originalRequest._authGeneration ?? getSessionGeneration()
    if (!originalRequest._authMutation) assertSessionGeneration(generation)
    if (error.response?.status === 401 && !originalRequest._retry && !skipAuthRefresh && originalRequest._authRefreshAllowed) {
      originalRequest._retry = true
      // A delayed 401 may belong to the token a completed refresh replaced.
      // Replay it once with the current token instead of rotating again.
      const currentToken = getAccessToken()
      if (currentToken && originalRequest.headers.Authorization !== `Bearer ${currentToken}`) return api(originalRequest)
      try {
        await refreshAuthSession(async (refreshGeneration) => {
          const refreshConfig: AuthRefreshConfig = {
            _skipAuthRefresh: true,
            _authGeneration: refreshGeneration,
            _authMutation: true,
          }
          const response = await api.post('/auth/refresh', undefined, refreshConfig)
          return response.data
        })
        assertSessionGeneration(generation)
        return api(originalRequest)
      } catch (refreshError) {
        // Only this session's failed refresh may clear state or redirect. Other
        // callers of its shared promise see the invalidated generation too.
        if (getSessionGeneration() === generation) {
          clearAccessToken()
          localStorage.removeItem('current_org')
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export { api, feedClient, FEED_API_URL }
export default api
